import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import { logout } from '../slices/authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) headers.set('authorization', `Bearer ${token}`);
    return headers;
  },
});

/**
 * Wraps fetchBaseQuery so an expired/invalid session (401 on an authenticated
 * endpoint) forces a clean re-login instead of failing silently — every screen
 * was otherwise left showing stale data with no signal that the session died.
 * Not used by authApi: a 401 from /auth/login is wrong credentials, not session
 * expiry, and must stay a normal form error rather than trigger a redirect loop.
 */
export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args, api, extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    localStorage.removeItem('accessToken');
    api.dispatch(logout());
    if (!window.location.pathname.startsWith('/auth/')) {
      window.location.href = '/auth/login';
    }
  }

  return result;
};
